import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Edit3,
  Trash2,
  Search,
  AlertCircle,
  Loader2,
} from "lucide-react";
import MenuFormModal from "./MenuFormModal";
import menuService from "@/services/menuService";
import profileService from "@/services/profileService";
import { showError, showInfo } from "@/store/errorStore";

/**
 * Erros HTTP já são exibidos pelo modal global (axiosInterceptor);
 * só mostramos aqui erros sem resposta do servidor (ex.: falha local).
 */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Erro");
  }
};

/**
 * CRUD dos itens do menu lateral (bf_menu)
 */
export default function MenusManagement({
  menus = [],
  profiles = [],
  loading,
  onReload,
  isOwner = false,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingMenuId, setLoadingMenuId] = useState(null);
  const [deletingMenu, setDeletingMenu] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const nextOrder =
    menus.reduce((max, menu) => Math.max(max, menu.order ?? 0), 0) + 1;

  const filteredMenus = menus.filter((menu) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      (menu.label || "").toLowerCase().includes(query) ||
      (menu.link || "").toLowerCase().includes(query)
    );
  });

  const afterChange = async () => {
    await onReload();
    menuService.notifyMenusChanged();
  };

  const handleCreate = () => {
    setEditingMenu(null);
    setShowModal(true);
  };

  /**
   * Abrir modal de edição (busca dados atualizados por ID)
   */
  const handleEdit = async (menu) => {
    setLoadingMenuId(menu.id);
    try {
      const fresh = await menuService.getMenuById(menu.id);
      setEditingMenu(fresh || menu);
      setShowModal(true);
    } catch (error) {
      showLocalError(error, "Erro ao carregar menu");
    } finally {
      setLoadingMenuId(null);
    }
  };

  const handleSave = async (menuData) => {
    setSaving(true);
    try {
      if (editingMenu) {
        await menuService.updateMenu(editingMenu.id, menuData);
        showInfo(
          `Menu "${menuData.label}" atualizado com sucesso`,
          "Menu Atualizado",
        );
      } else {
        await menuService.createMenu(menuData);
        showInfo(`Menu "${menuData.label}" criado com sucesso`, "Menu Criado");
      }
      setShowModal(false);
      setEditingMenu(null);
      await afterChange();
    } catch (error) {
      showLocalError(error, "Erro ao salvar menu");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (menu) => {
    setDeletingMenu(menu);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingMenu) return;

    setDeleting(true);
    try {
      await menuService.deleteMenu(deletingMenu.id);
      showInfo(
        `Menu "${deletingMenu.label}" removido com sucesso`,
        "Menu Removido",
      );
      await afterChange();
    } catch (error) {
      showLocalError(error, "Erro ao remover menu");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeletingMenu(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between space-y-0">
          <div>
            <CardTitle>Menus</CardTitle>
            <CardDescription>
              Itens do menu lateral do painel. O link precisa corresponder a uma
              tela existente.
            </CardDescription>
          </div>
          {isOwner ? (
            <Button
              onClick={handleCreate}
              className="bg-[#1b5e20] hover:bg-[#144919] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Menu
            </Button>
          ) : (
            <span className="text-sm text-gray-500">
              Somente consulta: menus são alterados pelo proprietário do
              sistema
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder="Buscar por nome ou link..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando menus...</p>
              </div>
            </div>
          ) : filteredMenus.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">
                {searchQuery
                  ? "Nenhum menu encontrado"
                  : "Nenhum menu cadastrado"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Ordem</TableHead>
                    <TableHead>Menu</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Perfis</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMenus.map((menu) => {
                    const IconComponent = menuService.resolveIcon(menu.icon);
                    const roles = menu.roles || [];
                    return (
                      <TableRow key={menu.id}>
                        <TableCell className="font-mono text-sm text-gray-500">
                          {menu.order}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 font-medium">
                            <IconComponent className="w-4 h-4 text-gray-600" />
                            {menu.label}
                          </div>
                          <div className="text-xs text-gray-400">
                            {menu.icon}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {menu.link}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {roles.length > 0 ? (
                              roles.map((role) => (
                                <Badge key={role} variant="outline">
                                  {profileService.getProfileLabel(role)}
                                </Badge>
                              ))
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-amber-50 text-amber-800 border-amber-200"
                              >
                                Todos
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {menu.enabled === false ? (
                            <Badge
                              variant="outline"
                              className="bg-gray-100 text-gray-600 border-gray-200"
                            >
                              Inativo
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-green-100 text-green-800 border-green-200"
                            >
                              Ativo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(menu)}
                              disabled={!isOwner || loadingMenuId === menu.id}
                              title={
                                isOwner ? undefined : "Somente o proprietário do sistema (ROLE_OWNER) pode alterar menus"
                              }
                            >
                              {loadingMenuId === menu.id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Edit3 className="w-4 h-4 mr-1" />
                              )}
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteClick(menu)}
                              disabled={!isOwner}
                              title={
                                isOwner ? undefined : "Somente o proprietário do sistema (ROLE_OWNER) pode alterar menus"
                              }
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Excluir
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <MenuFormModal
        open={showModal}
        onOpenChange={setShowModal}
        menu={editingMenu}
        profiles={profiles}
        nextOrder={nextOrder}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o menu{" "}
              <strong>{deletingMenu?.label}</strong>? As permissões dos perfis
              para este menu também serão removidas e ele deixará de aparecer
              para todos os usuários.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeletingMenu(null)}
              disabled={deleting}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? "Excluindo..." : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
